import { useState } from "react";
import { Loader2, X, Plus, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}` });

const TYPE_OPTIONS = [
  { value: "pickup", label: "Pick-up" },
  { value: "dropoff", label: "Drop-off" },
  { value: "delivery", label: "Delivery" },
  { value: "trial", label: "Trial" },
];

export function AdminAppointmentCreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    appointment_type: "pickup",
    date: "",
    time: "",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    delivery_address: "",
    notes: "",
    duration_minutes: "",
    skip_overlap_check: false,
  });
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const submit = async () => {
    if (!form.date || !form.time) {
      toast.error("Date and time are required");
      return;
    }
    setSaving(true);
    setConflict(null);
    try {
      const body = { ...form };
      if (body.duration_minutes === "" || body.duration_minutes == null) {
        delete body.duration_minutes;
      } else {
        body.duration_minutes = Number(body.duration_minutes);
      }
      const r = await axios.post(`${API}/admin/appointments/create`, body, { headers: auth() });
      toast.success("Appointment created");
      onCreated?.(r.data);
      onClose?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && typeof detail === "object") {
        setConflict(detail);
      } else {
        toast.error(typeof detail === "string" ? detail : "Create failed");
      }
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose} data-testid="appt-create-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between p-5 border-b border-[#282828]/8">
          <div>
            <h2 className="text-[15px] text-[#282828] font-medium tracking-wide" data-testid="appt-create-title">New Appointment</h2>
            <p className="text-[11px] text-[#676664]/60 mt-1">Manually create an appointment on behalf of a customer.</p>
          </div>
          <button onClick={onClose} className="text-[#676664]/40 hover:text-[#282828] p-1" data-testid="appt-create-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Type *</label>
              <select value={form.appointment_type} onChange={set("appointment_type")} className="w-full border border-[#282828]/12 px-3 py-2 text-[12px]" data-testid="create-type">
                {TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Duration (min)</label>
              <input type="number" min="15" max="240" placeholder="auto" value={form.duration_minutes} onChange={set("duration_minutes")} className="w-full border border-[#282828]/12 px-3 py-2 text-[12px]" data-testid="create-duration" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Date *</label>
              <input type="date" value={form.date} onChange={set("date")} className="w-full border border-[#282828]/12 px-3 py-2 text-[12px]" data-testid="create-date" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Time *</label>
              <input type="time" value={form.time} onChange={set("time")} className="w-full border border-[#282828]/12 px-3 py-2 text-[12px]" data-testid="create-time" />
            </div>
            <div className="col-span-2">
              <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Customer name</label>
              <input value={form.customer_name} onChange={set("customer_name")} className="w-full border border-[#282828]/12 px-3 py-2 text-[12px]" data-testid="create-cname" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Email</label>
              <input value={form.customer_email} onChange={set("customer_email")} className="w-full border border-[#282828]/12 px-3 py-2 text-[12px]" data-testid="create-email" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Phone</label>
              <input value={form.customer_phone} onChange={set("customer_phone")} className="w-full border border-[#282828]/12 px-3 py-2 text-[12px]" data-testid="create-phone" />
            </div>
            {form.appointment_type === "delivery" && (
              <div className="col-span-2">
                <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Delivery address</label>
                <input value={form.delivery_address} onChange={set("delivery_address")} className="w-full border border-[#282828]/12 px-3 py-2 text-[12px]" data-testid="create-address" />
              </div>
            )}
            <div className="col-span-2">
              <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Notes</label>
              <textarea rows={2} value={form.notes} onChange={set("notes")} className="w-full border border-[#282828]/12 px-3 py-2 text-[12px] resize-none" data-testid="create-notes" />
            </div>
            <label className="col-span-2 flex items-center gap-2 text-[11px] text-[#676664]/80">
              <input type="checkbox" checked={form.skip_overlap_check} onChange={(e) => setForm({ ...form, skip_overlap_check: e.target.checked })} data-testid="create-skip-overlap" />
              Skip overlap check (force create even if conflicting)
            </label>
          </div>

          {conflict && (
            <div className="bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-800" data-testid="create-conflict">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                <div className="flex-1">
                  <div className="font-medium">{conflict.message}</div>
                  {conflict.suggestions?.length > 0 && (
                    <div className="mt-2">
                      Suggested times on {conflict.date}:
                      <div className="flex flex-wrap gap-1 mt-1">
                        {conflict.suggestions.map(s => (
                          <button key={s} onClick={() => setForm({ ...form, time: s })} className="px-2 py-0.5 bg-white border border-amber-300 text-amber-700 hover:bg-amber-100" data-testid={`suggest-${s}`}>{s}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mt-2 text-[10px]">Or check "Skip overlap check" above to force-create.</div>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-3 border-t border-[#282828]/8">
            <Button variant="outline" onClick={onClose} disabled={saving} className="text-[11px] h-8 px-4" data-testid="create-cancel">Cancel</Button>
            <Button onClick={submit} disabled={saving || !form.date || !form.time} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] h-8 px-4" data-testid="create-submit">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />} Create appointment
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
